/**
 * フェイズカード3段階目チャージ有効性チェッカー
 *
 * AIがフェイズカードに3枚目のカードをチャージする前に、
 * そのフェイズカードの3段階目効果が実際に有効かをチェックするシステム
 *
 * 判定基準:
 * 1. リーサル判定: このターンで相手LPを0にできるか
 *    - 貫通ダメージ（公式ルール6.3）
 *    - 孤軍奮闘補正（公式ルール6.2）
 *    - 設置物軽減（75%）
 *    - フェイズカード効果（ATKバフ、AoEダメージ）
 * 2. 非リーサル時の条件: カード固有の有効性チェック
 */

import type { MonsterInstance, PhaseCardInstance, CardAttribute } from '../../types/card';
import type { AIGameState } from './strategies/base';

/** ATKバフ条件型 */
interface AtkBuff {
  condition: (monster: MonsterInstance) => boolean;
  amount: number;
}

/** フェイズカード3段階目チェッカー関数型 */
type PhaseCardFinalStageChecker = (gameState: AIGameState) => boolean;

// ============================================================
// ヘルパー関数: フィールド情報取得
// ============================================================

/**
 * 自分の場に指定属性のモンスターがいるかチェック
 */
const hasMonsterWithAttribute = (gameState: AIGameState, attribute: CardAttribute): boolean => {
  return gameState.myField.some(monster =>
    monster && monster.attribute === attribute
  );
};

/**
 * 自分の場に指定カテゴリのモンスターがいるかチェック
 */
const hasMonsterWithCategory = (gameState: AIGameState, categoryKeyword: string): boolean => {
  return gameState.myField.some(monster =>
    monster && monster.category && monster.category.includes(categoryKeyword)
  );
};

/**
 * 自分の場に指定名を含むモンスターがいるかチェック
 */
const hasMonsterWithName = (gameState: AIGameState, nameKeyword: string): boolean => {
  return gameState.myField.some(monster =>
    monster && monster.name && monster.name.includes(nameKeyword)
  );
};

/**
 * 相手の場にモンスターがいるかチェック
 */
const hasOpponentMonster = (gameState: AIGameState): boolean => {
  return gameState.opponentField.some(monster => monster !== null);
};

/**
 * 相手の場のモンスター数をカウント
 */
const countOpponentMonsters = (gameState: AIGameState): number => {
  return gameState.opponentField.filter(monster => monster !== null).length;
};

/**
 * 自分の場のモンスター数をカウント
 */
const countMyMonsters = (gameState: AIGameState): number => {
  return gameState.myField.filter(monster => monster !== null).length;
};

/**
 * 自分の場に空きがあるかチェック
 */
const hasEmptySlot = (gameState: AIGameState): boolean => {
  return gameState.myField.some(monster => monster === null);
};

/**
 * 自分の場に複数属性のいずれかのモンスターがいるかチェック
 */
const hasMonsterWithAnyAttribute = (gameState: AIGameState, attributes: CardAttribute[]): boolean => {
  return gameState.myField.some(monster =>
    monster && attributes.includes(monster.attribute)
  );
};

/**
 * 指定カテゴリのモンスター数をカウント
 */
const countMonsterWithCategory = (gameState: AIGameState, categoryKeyword: string): number => {
  return gameState.myField.filter(monster =>
    monster && monster.category && monster.category.includes(categoryKeyword)
  ).length;
};

/**
 * 指定属性のモンスター数をカウント
 */
const countMonsterWithAttribute = (gameState: AIGameState, attribute: CardAttribute): number => {
  return gameState.myField.filter(monster =>
    monster && monster.attribute === attribute
  ).length;
};

/**
 * 複数属性のモンスター数をカウント
 */
const countMonsterWithAnyAttribute = (gameState: AIGameState, attributes: CardAttribute[]): number => {
  return gameState.myField.filter(monster =>
    monster && attributes.includes(monster.attribute)
  ).length;
};

// ============================================================
// リーサル計算システム
// ============================================================

/**
 * 孤軍奮闘ボーナスを計算（公式ルール6.2）
 */
const calculateUnderdogBonus = (myCount: number, enemyCount: number): number => {
  if (myCount >= enemyCount) return 1.0;
  const diff = enemyCount - myCount;
  if (diff >= 3) return 1.4;
  if (diff === 2) return 1.25;
  if (diff === 1) return 1.1;
  return 1.0;
};

/**
 * リーサルダメージを計算
 * 貫通ダメージ、孤軍奮闘、設置物軽減を考慮
 */
const calculateLethalDamage = (
  gameState: AIGameState,
  atkBuffs: AtkBuff[] = [],
  aoeDamage: number = 0
): number => {
  // フィールドをコピー（シミュレーション用）
  const myField = [...gameState.myField];
  let opponentField = gameState.opponentField.map(m => m ? { ...m } : null);

  // 設置物の存在確認
  const hasOpponentSetup = gameState.opponentPhaseCard || gameState.opponentFieldCard;

  let totalDamageToLP = 0;

  // ① AoEダメージで敵を事前処理（アクアレギナ2000、永遠の灯800など）
  if (aoeDamage > 0) {
    opponentField = opponentField.map(enemy => {
      if (!enemy) return null;
      const currentHp = enemy.currentHp !== undefined ? enemy.currentHp : (enemy.hp || 0);
      const newHp = currentHp - aoeDamage;
      if (newHp <= 0) return null; // 撃破
      return { ...enemy, currentHp: newHp };
    });
  }

  // ② 攻撃可能モンスターをATK順にソート（高ATK順で貫通ダメージ最大化）
  const attackers = myField
    .filter((m): m is MonsterInstance => m !== null && m.canAttack)
    .map(m => {
      let atk = m.currentAttack !== undefined ? m.currentAttack : (m.attack || 0);
      // フェイズカードバフを適用
      for (const buff of atkBuffs) {
        if (buff.condition(m)) {
          atk += buff.amount;
        }
      }
      return { monster: m, effectiveAtk: atk };
    })
    .sort((a, b) => b.effectiveAtk - a.effectiveAtk); // 高ATK順

  // ③ 各攻撃をシミュレート
  for (const attacker of attackers) {
    const enemies = opponentField.filter((e): e is MonsterInstance => e !== null);
    const myCount = myField.filter((m): m is MonsterInstance => m !== null).length;
    const enemyCount = enemies.length;

    // 孤軍奮闘ボーナス計算
    const atkMultiplier = calculateUnderdogBonus(myCount, enemyCount);
    const finalAtk = Math.floor(attacker.effectiveAtk * atkMultiplier);

    if (enemies.length === 0) {
      // ダイレクトアタック
      let directDamage = finalAtk;
      if (hasOpponentSetup) {
        directDamage = Math.floor(directDamage * 0.75); // 設置物あり: 75%
      }
      totalDamageToLP += directDamage;
    } else {
      // 最もHPが低い敵を攻撃（貫通ダメージ最大化）
      enemies.sort((a, b) => {
        const hpA = a.currentHp !== undefined ? a.currentHp : (a.hp || 0);
        const hpB = b.currentHp !== undefined ? b.currentHp : (b.hp || 0);
        return hpA - hpB;
      });

      const target = enemies[0];
      const targetHp = target.currentHp !== undefined ? target.currentHp : (target.hp || 0);

      if (finalAtk > targetHp) {
        // 撃破 + 貫通ダメージ（公式ルール6.3: 余剰の50%）
        const excess = finalAtk - targetHp;
        const penetration = Math.floor(excess * 0.5);
        totalDamageToLP += penetration;

        // 敵を除去（次の攻撃シミュレーション用）
        const idx = opponentField.findIndex(e => e === target);
        if (idx !== -1) {
          opponentField[idx] = null;
        }
      }
      // 撃破できない場合はLPダメージなし
    }
  }

  return totalDamageToLP;
};

/**
 * リーサル判定: このターンで相手LPを0にできるか
 */
const canDealLethal = (
  gameState: AIGameState,
  atkBuffs: AtkBuff[] = [],
  aoeDamage: number = 0
): boolean => {
  const opponentLife = gameState.opponentLife;
  const damage = calculateLethalDamage(gameState, atkBuffs, aoeDamage);
  return damage >= opponentLife;
};

// ============================================================
// カードIDごとの3段階目チャージ有効性チェッカー
// ============================================================

const phaseCardFinalStageCheckers: Record<string, PhaseCardFinalStageChecker> = {
  /**
   * C0000108: 虚蝕の呪詛
   * 3段階目: 相手モンスター1体ATK0 + 闇属性ATK+800 + 自分ライフ-1000
   *
   * リーサル条件: 闇属性にATK+800バフ適用でリーサル
   * 非リーサル条件: 相手モンスター≥1 AND 闇属性≥2
   */
  C0000108: (gameState) => {
    // リーサル判定: 闇属性に+800
    const atkBuffs = [
      { condition: (m: MonsterInstance) => m.attribute === '闇', amount: 800 }
    ];
    if (canDealLethal(gameState, atkBuffs, 0)) {
      return true;
    }

    // 非リーサル: 敵がいて闇モンスターが2体以上
    const enemyCount = countOpponentMonsters(gameState);
    const darkCount = countMonsterWithAttribute(gameState, '闇');
    return enemyCount >= 1 && darkCount >= 2;
  },

  /**
   * C0000185: 岩狸の山里
   * 3段階目: 【エンドフェイズ】岩狸1体につき相手に600ダメージ
   *
   * リーサル条件: バトルダメージ + 岩狸×600 >= 相手LP
   * 非リーサル条件: 岩狸≥2
   */
  C0000185: (gameState) => {
    const rockTanukiCount = countMonsterWithCategory(gameState, '岩狸');

    // エンドフェイズダメージ（バトル後）
    const endPhaseDamage = rockTanukiCount * 600;

    // バトルダメージ計算（ATKバフなし）
    const battleDamage = calculateLethalDamage(gameState, [], 0);

    // リーサル判定
    if (battleDamage + endPhaseDamage >= gameState.opponentLife) {
      return true;
    }

    // 非リーサル: 岩狸2体以上
    return rockTanukiCount >= 2;
  },

  /**
   * C0000230: 永遠の灯の祈り
   * 3段階目: 光属性HP+1000 + 相手フィールド全体800ダメージ
   *
   * リーサル条件: AoE800ダメージ適用後でリーサル
   * 非リーサル条件: 光≥2 AND 敵≥2
   */
  C0000230: (gameState) => {
    // リーサル判定: 相手全体800ダメージ
    if (canDealLethal(gameState, [], 800)) {
      return true;
    }

    // 非リーサル: 光2体以上 AND 敵2体以上
    const lightCount = countMonsterWithAttribute(gameState, '光');
    const enemyCount = countOpponentMonsters(gameState);
    return lightCount >= 2 && enemyCount >= 2;
  },

  /**
   * C0000266: エクラシアの時空炉
   * 3段階目: 未来属性ATK+2000 + 相手全体ATK-1500 + 自分2000ダメージ
   *
   * リーサル条件: 未来属性にATK+2000バフ適用でリーサル
   * 非リーサル条件: 未来≥2
   */
  C0000266: (gameState) => {
    // リーサル判定: 未来属性に+2000
    const atkBuffs = [
      { condition: (m: MonsterInstance) => m.attribute === '未来', amount: 2000 }
    ];
    if (canDealLethal(gameState, atkBuffs, 0)) {
      return true;
    }

    // 非リーサル: 未来2体以上
    const futureCount = countMonsterWithAttribute(gameState, '未来');
    return futureCount >= 2;
  },

  /**
   * C0000339: アクアレギナの動力-エテルノス・コア
   * 3段階目: 相手フィールド全体2000ダメージ + 自分ライフ-1000
   *
   * リーサル条件: AoE2000ダメージ適用後でリーサル
   * 非リーサル条件: 敵≥2
   */
  C0000339: (gameState) => {
    // リーサル判定: 相手全体2000ダメージ
    if (canDealLethal(gameState, [], 2000)) {
      return true;
    }

    // 非リーサル: 敵2体以上
    return countOpponentMonsters(gameState) >= 2;
  },

  /**
   * C0000352: アヴィクルスの試練
   * 3段階目: 相手モンスター全体行動不能 + 手札上限-2 + 自分1000ダメージ
   *
   * このカードはダメージ効果ではなく妨害効果
   * 条件: 敵≥2（行動不能の価値）
   */
  C0000352: (gameState) => {
    return countOpponentMonsters(gameState) >= 2;
  },

  /**
   * C0000383: 虹羽密林の共鳴弦
   * 3段階目: 水/光/炎属性モンスター全体ATK+500（永続）
   *
   * リーサル条件: 水/光/炎にATK+500バフ適用でリーサル
   * 非リーサル条件: 水/光/炎≥3
   */
  C0000383: (gameState) => {
    const targetAttributes: CardAttribute[] = ['水', '光', '炎'];

    // リーサル判定: 対象属性に+500
    const atkBuffs = [
      { condition: (m: MonsterInstance) => targetAttributes.includes(m.attribute), amount: 500 }
    ];
    if (canDealLethal(gameState, atkBuffs, 0)) {
      return true;
    }

    // 非リーサル: 対象3体以上
    return countMonsterWithAnyAttribute(gameState, targetAttributes) >= 3;
  },

  /**
   * C0000387: 呪核の杖・ザルヴェリオ
   * 3段階目: 呪骸王エリザルヴェドンがいればATK+1500、いなければ召喚
   *
   * 常に有効（強化または召喚のどちらかが発動）
   */
  C0000387: (gameState) => {
    const hasElizarvedon = hasMonsterWithName(gameState, '呪骸王エリザルヴェドン');
    if (hasElizarvedon) {
      return true; // 強化効果
    }
    // 召喚効果（空きが必要）
    return hasEmptySlot(gameState);
  },

  /**
   * C0000395: 血呪の棺・ヴェルディクタス
   * 3段階目: LP-1000 + エリザヴェット・ヴェイルを召喚
   *
   * 条件: ヴェイルがいない AND 空きスロットがある
   */
  C0000395: (gameState) => {
    const hasVeil = hasMonsterWithName(gameState, 'エリザヴェット・ヴェイル');
    if (hasVeil) {
      return false; // 既にいる場合は不要
    }
    return hasEmptySlot(gameState);
  },

  /**
   * C0000571: 彷徨いの儀式
   * 3段階目: 相手の【彷徨い】状態のモンスターを全て破壊し、破壊した数×600ダメージ
   *
   * 条件: 相手場に【彷徨い】状態のモンスターが1体以上いる
   */
  C0000571: (gameState) => {
    const wanderingCount = gameState.opponentField.filter(monster => {
      if (!monster) return false;
      return monster.statusEffects?.some(
        (s: { type: string }) => s.type === 'WANDERING'
      );
    }).length;
    return wanderingCount >= 1;
  },
};

// ============================================================
// エクスポート関数
// ============================================================

/**
 * 3段階目チャージが有効かどうかをチェック
 */
export function isFinalStageEffective(
  phaseCard: PhaseCardInstance | null | undefined,
  gameState: AIGameState
): boolean {
  if (!phaseCard || !phaseCard.id) {
    return true; // チェックできない場合はデフォルトで有効
  }

  const checker = phaseCardFinalStageCheckers[phaseCard.id];
  if (!checker) {
    return true; // チェッカーが未定義の場合はデフォルトで有効
  }

  try {
    return checker(gameState);
  } catch (error) {
    console.warn(`フェイズカード3段階目チェックでエラー: ${phaseCard.name}`, error);
    return true; // エラー時はデフォルトで有効
  }
}

/**
 * 2段階目から3段階目へのチャージを許可するかチェック
 * （2段階目の状態から3枚目をチャージするかの判断）
 */
export function shouldChargeFinalStage(
  phaseCard: PhaseCardInstance | null | undefined,
  gameState: AIGameState
): boolean {
  if (!phaseCard) {
    return false;
  }

  const currentStage = phaseCard.stage !== undefined
    ? phaseCard.stage
    : (phaseCard.charges?.length || 0);

  // 2段階目以外からはこの関数は呼ばれないはずだが念のため
  if (currentStage !== 2) {
    return true; // 2段階目以外は通常のチャージ判断を使用
  }

  return isFinalStageEffective(phaseCard, gameState);
}

/**
 * フェイズカードのチャージが推奨されるかチェック
 * 通常の1→2段階目チャージは常に許可、2→3段階目のみ条件チェック
 */
export function isPhaseCardChargeRecommended(
  phaseCard: PhaseCardInstance | null | undefined,
  gameState: AIGameState
): boolean {
  if (!phaseCard) {
    return false;
  }

  const currentStage = phaseCard.stage !== undefined
    ? phaseCard.stage
    : (phaseCard.charges?.length || 0);

  // 0→1, 1→2 は常に推奨
  if (currentStage < 2) {
    return true;
  }

  // 2→3 は条件チェック
  if (currentStage === 2) {
    return shouldChargeFinalStage(phaseCard, gameState);
  }

  // 3段階目（最終）は既にチャージ不可
  return false;
}

/**
 * リーサル計算をエクスポート（テスト・デバッグ用）
 */
export { calculateLethalDamage, canDealLethal };

export default {
  isFinalStageEffective,
  shouldChargeFinalStage,
  isPhaseCardChargeRecommended,
  calculateLethalDamage,
  canDealLethal,
  // 外部からチェッカーを追加可能にする
  addChecker: (cardId: string, checker: PhaseCardFinalStageChecker) => {
    phaseCardFinalStageCheckers[cardId] = checker;
  },
};
